import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import {
  PostAttachment,
  PostItem as PostItemType,
  deletePost,
  getAttachmentDownloadUrl,
  getAttachmentViewUrl,
  updatePost,
  uploadPostAttachments,
} from '../../api/posts'
import PostMarkdownEditor from './PostMarkdownEditor'

interface Props {
  post: PostItemType
  canEdit: boolean
  onUpdated: () => void
}

const formatDateTime = (value?: string) => {
  if (!value) return '—'
  return new Date(value).toLocaleString('ru-RU')
}

export default function PostItem({ post, canEdit, onUpdated }: Props) {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState(post.content)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [editAttachments, setEditAttachments] = useState<PostAttachment[]>(post.attachments || [])

  useEffect(() => {
    setEditText(post.content)
    setEditAttachments(post.attachments || [])
  }, [post.content, post.attachments])

  const handleSave = async () => {
    try {
      setIsSaving(true)
      await updatePost(post.id, editText)
      setIsEditing(false)
      onUpdated()
    } catch (error) {
      console.error('❌ Не удалось обновить пост:', error)
      alert('Не удалось обновить пост')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Удалить пост? Это действие нельзя отменить.')) return
    try {
      setIsDeleting(true)
      await deletePost(post.id)
      onUpdated()
    } catch (error) {
      console.error('❌ Не удалось удалить пост:', error)
      alert('Не удалось удалить пост')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleUpload = async (files: File[]) => {
    if (!files.length) return
    try {
      setIsUploading(true)
      const uploaded = await uploadPostAttachments(post.id, files)
      setEditAttachments((prev) => [...prev, ...uploaded])
    } catch (error) {
      console.error('❌ Не удалось загрузить файлы при редактировании:', error)
      alert(`Не удалось загрузить файлы: ${error instanceof Error ? error.message : 'неизвестная ошибка'}`)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="border rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="text-sm text-gray-600">
          {post.author_name && (
            <div className="font-semibold text-gray-800 flex items-center gap-1.5 mb-0.5">
              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                {post.author_name[0].toUpperCase()}
              </span>
              <span>{post.author_name}</span>
            </div>
          )}
          <div className="text-xs text-gray-500">
            {formatDateTime(post.created_at)}
            {post.updated_at && post.updated_at !== post.created_at && (
              <span className="text-gray-400 ml-1.5">(изм. {formatDateTime(post.updated_at)})</span>
            )}
          </div>
        </div>
        {canEdit && !isEditing && (
          <div className="flex gap-2">
            <button
              className="text-sm px-3 py-1 rounded bg-gray-100 hover:bg-gray-200"
              onClick={() => setIsEditing(true)}
            >
              Редактировать
            </button>
            <button
              className="text-sm px-3 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Удаляю...' : 'Удалить'}
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-2">
          <PostMarkdownEditor
            value={editText}
            onChange={setEditText}
            attachments={editAttachments}
            onFilesPicked={handleUpload}
            isUploading={isUploading}
          />
          <div className="flex gap-2">
            <button
              className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Сохраняю...' : 'Сохранить'}
            </button>
            <button
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
              onClick={() => {
                setEditText(post.content)
                setEditAttachments(post.attachments || [])
                setIsEditing(false)
              }}
              disabled={isSaving}
            >
              Отмена
            </button>
          </div>
        </div>
      ) : (
        <div className="prose prose-sm max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={{
              a: ({ href, children }) => {
                const hrefValue = href || '#'
                const downloadHref = hrefValue.includes('?')
                  ? hrefValue.replace('mode=view', 'mode=download')
                  : hrefValue
                return (
                  <span className="inline-flex items-center gap-1">
                    <a href={hrefValue} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {children}
                    </a>
                    <a href={downloadHref} title="Скачать" className="text-blue-600 hover:text-blue-800" aria-label="Скачать файл">
                      ⬇
                    </a>
                  </span>
                )
              },
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>
      )}

      {(isEditing ? editAttachments : post.attachments).length > 0 && (
        <div className="mt-4 pt-3 border-t">
          <div className="text-sm font-medium text-gray-800 mb-2">Прикрепленные файлы:</div>
          <ul className="space-y-1">
            {(isEditing ? editAttachments : post.attachments).map((file) => (
              <li key={file.id} className="text-sm">
                <a
                  href={getAttachmentViewUrl(file.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {file.original_filename}
                </a>
                <a href={getAttachmentDownloadUrl(file.id)} className="ml-2 text-blue-600 hover:text-blue-800" title="Скачать">
                  ⬇
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
